import { useEffect, useState } from "react";
import { Heart, RotateCcw, Search, X } from "lucide-react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LIFESTYLES } from "@/lib/site";
import { compactMoney } from "@/lib/format";
import { matchBase } from "@/components/home/Hero";

const PRICES = [100000, 200000, 250000, 300000, 350000, 400000, 450000, 500000, 600000, 700000, 800000, 1000000, 1500000];
const trig = "h-10 w-auto min-w-[120px] gap-2 rounded-full border-neutral-300 bg-white px-4 text-[14px] data-[state=open]:border-ink";

const Pick = ({ value, onChange, placeholder, options, testId, groups }) => (
  <Select value={value || "any"} onValueChange={(v) => onChange(v === "any" ? null : v)}>
    <SelectTrigger className={`${trig} ${value ? "border-ink" : ""}`} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="any">{placeholder}</SelectItem>
      {groups
        ? groups.map((g) => (
          <SelectGroup key={g.label}><SelectLabel>{g.label}</SelectLabel>{g.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectGroup>
        ))
        : options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const PriceFilter = ({ min, max, update }) => {
  const label = min || max ? `${min ? compactMoney(Number(min)) : "Any"} – ${max ? compactMoney(Number(max)) : "Any"}` : "Price";
  const opts = PRICES.map((p) => [String(p), compactMoney(p)]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`flex h-10 shrink-0 items-center rounded-full border px-4 text-[14px] ${min || max ? "border-ink" : "border-neutral-300"} bg-white`} data-testid="filter-price">{label}</button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <p className="mb-3 text-[13px] text-neutral-600">Price range</p>
        <div className="grid grid-cols-2 gap-2">
          <Pick value={min} onChange={(v) => update({ min_price: v })} placeholder="No min" options={opts} testId="filter-price-min" />
          <Pick value={max} onChange={(v) => update({ max_price: v })} placeholder="No max" options={opts} testId="filter-price-max" />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const FilterBar = ({ filters, update, bases, reset }) => {
  const [q, setQ] = useState(filters.q || "");
  useEffect(() => {
    setQ(filters.q || "");
  }, [filters.q]);
  const regions = [...new Set(bases.map((b) => b.region))];

  const submit = (e) => {
    e.preventDefault();
    const base = matchBase(bases, q);
    if (base) {
      update({ base: base.id, radius: filters.radius || "15", q: null, sort: "distance" });
      setQ("");
    } else update({ q: q.trim() || null });
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto border-b border-neutral-200 bg-white px-4 py-3 no-scrollbar sm:px-6" data-testid="filter-bar">
      <form onSubmit={submit} className="flex h-10 w-[260px] shrink-0 items-center rounded-full border border-neutral-300 bg-white pl-4 pr-1 focus-within:border-ink">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Base, city, neighborhood, ZIP" data-testid="filter-search-input"
          className="min-w-0 flex-1 bg-transparent text-[14px] focus:outline-none" />
        <button type="submit" aria-label="Search" data-testid="filter-search-submit" className="grid h-8 w-8 place-items-center rounded-full bg-ink text-white"><Search className="h-3.5 w-3.5" /></button>
      </form>
      <Pick value={filters.base} onChange={(v) => update({ base: v, radius: v ? filters.radius || "15" : null, sort: v ? "distance" : null })} placeholder="Near any base" testId="filter-base"
        groups={regions.map((r) => ({ label: r, options: bases.filter((b) => b.region === r).map((b) => [b.id, b.short]) }))} />
      <Select value={filters.radius || "15"} onValueChange={(v) => update({ radius: v })} disabled={!filters.base}>
        <SelectTrigger className={`${trig} min-w-[110px]`} data-testid="filter-radius"><SelectValue /></SelectTrigger>
        <SelectContent>{["5", "10", "15", "20", "30"].map((r) => <SelectItem key={r} value={r}>Within {r} mi</SelectItem>)}</SelectContent>
      </Select>
      <PriceFilter min={filters.min_price} max={filters.max_price} update={update} />
      <Pick value={filters.beds} onChange={(v) => update({ beds: v })} placeholder="Beds" testId="filter-beds" options={[1, 2, 3, 4, 5].map((n) => [String(n), `${n}+ beds`])} />
      <Pick value={filters.baths} onChange={(v) => update({ baths: v })} placeholder="Baths" testId="filter-baths" options={[1, 2, 3].map((n) => [String(n), `${n}+ baths`])} />
      <Pick value={filters.ptype} onChange={(v) => update({ ptype: v })} placeholder="Home type" testId="filter-type" options={[["Single Family", "Single family"], ["Townhouse", "Townhouse"], ["Condo", "Condo"]]} />
      <Pick value={filters.lifestyle} onChange={(v) => update({ lifestyle: v })} placeholder="Lifestyle" testId="filter-lifestyle" options={LIFESTYLES.map((l) => [l.id, l.label])} />
      <Pick value={filters.region} onChange={(v) => update({ region: v })} placeholder="All areas" testId="filter-region" options={[["Hampton Roads", "Hampton Roads"], ["Greater Richmond", "Greater Richmond"]]} />
      <button onClick={() => update({ saved: filters.saved ? null : "1" })} data-testid="filter-saved"
        className={`flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-4 text-[14px] ${filters.saved ? "border-ink bg-ink text-white" : "border-neutral-300 bg-white"}`}>
        <Heart className={`h-4 w-4 ${filters.saved ? "fill-white" : ""}`} /> Saved
      </button>
      {filters.kw && (
        <button onClick={() => update({ kw: null })} data-testid="filter-keywords"
          className="flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-ink bg-white px-4 text-[14px]">
          Must have: {filters.kw.split(",").join(", ")} <X className="h-3.5 w-3.5" />
        </button>
      )}
      <button onClick={reset} data-testid="filter-reset" className="flex h-10 shrink-0 items-center gap-1.5 px-3 text-[14px] text-neutral-600 hover:text-ink">
        <RotateCcw className="h-3.5 w-3.5" /> Reset
      </button>
    </div>
  );
};
